import PropTypes from 'prop-types'
import { withRouter } from 'next/router';
import Head from 'next/head';
import compose from 'lodash/flowRight';
import map from 'lodash/map';
import { graphql } from '@apollo/client/react/hoc';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Loading from '../../../../components/loading';
import ErrorMessage from '../../../../components/error-message';
import NotFound from '../../../../components/not-found';
import EventList from '../../../../components/event-list';
import { groupPastEventsQuery } from '../../../../apollo/groups';
import { buildTitle } from '../../../../utils/layout';
import GroupHeader from '../../../../components/groups/header';
import config from '../../../../config';
import { buildImageUrl } from '../../../../utils/images';
import consumeAppContext from '../../../../consumers/app';
import Main from '../../../../components/main';
import MaterialLinkButton from '../../../../components/material-link-button';
import SubscribeToCalendarButton from '../../../../components/subscribe-to-calendar-button';
import CalendarHeader from '../../../../components/groups/calendar-header';

const useStyles = makeStyles((theme) => ({
  eventsHeader: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  eventsHeaderRight: {
    display: 'flex',
  },
  otherEventsButton: {
    marginLeft: theme.spacing(1),
  },
}));

const buildJsonLdOrganization = (group) => {
  const ld = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": group.name,
    "url": `${config().universal.baseUri}/groups/${group.slug}`
  };
  if (group.image) {
    ld.logo = buildImageUrl(group.image, 'json-ld');
  }
  return ld;
};

const buildJsonLDBreadcrumbs = (group) => {
  const items = [
    {
      '@id': `${config().universal.baseUri}/cities/${group.city.slug}`,
      name: group.city.name,
    },
    {
      '@id': `${config().universal.baseUri}/groups/${group.slug}`,
      name: group.name,
    },
    {
      '@id': `${config().universal.baseUri}/groups/${group.slug}/calendar/past`,
      name: 'Past',
    },
  ];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: map(items, (item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item,
    })),
  };
};

const GroupPastEvents = (props) => {
  const {
    loading,
    error,
    group
  } = props;
  const classes = useStyles();

  if (loading) { return <Loading />; }
  if (error) {
    return <ErrorMessage message='An error occurred while loading group.' error={error} />;
  }
  if (!group || group.found) { return <NotFound />; }

  const canonicalUrl = `${config().universal.baseUri}/groups/${group.slug}/calendar/past`;

  return (
    <Main>
      <Head>
        <title>{buildTitle(`Past Events - ${group.name}`)}</title>
        {!group.discoverable && <meta name="robots" content="noindex" />}
        <link rel="canonical" href={canonicalUrl} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(buildJsonLdOrganization(group)),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(buildJsonLDBreadcrumbs(group)),
          }}
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={`Past Bike Rides and Events by ${group.name}`} />
        {group.image && (
          <meta property="og:image" content={buildImageUrl(group.image, 'facebook-share')} />
        )}
      </Head>

      <GroupHeader group={group} tab="calendar" />
      <CalendarHeader title="Calendar" tab="list" group={group} />

      <Typography variant="h2" className={classes.eventsHeader}>
        <div>Past</div>
        <div className={classes.eventsHeaderRight}>
          <MaterialLinkButton
            href="/groups/[slug]/calendar"
            as={`/groups/${group.slug}/calendar`}
            variant="outlined"
            size="small"
            className={classes.otherEventsButton}
          >
            Upcoming ({group.upcomingEventsCount})
          </MaterialLinkButton>
          {group.permissions.createEvent && (
            <MaterialLinkButton
              href={`/events/new?groupId=${group.id}`}
              variant="contained"
              size="small"
              color="primary"
              className={classes.otherEventsButton}
            >
              Add Event
            </MaterialLinkButton>
          )}
        </div>
      </Typography>

      <EventList
        events={group.pastEvents}
        group={group}
        emptyMessage={
          group.permissions.joined ? 'No past events' : 'No past public events'
        }
        fullDate
        highlightAdminEvents
      />

      <SubscribeToCalendarButton
        href="/groups/[slug]/calendar/subscribe"
        as={`/groups/${group.slug}/calendar/subscribe`}
      />
    </Main>
  );
}

GroupPastEvents.propTypes = {
  router: PropTypes.object,
  group: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.object,
};

export default compose(
  withRouter,
  consumeAppContext,
  graphql(groupPastEventsQuery, {
    props: ({ data: { group, loading, error, refetch } }) => {
      return {
        group,
        loading,
        error,
        refetch
      };
    },
    options: ({ router }) => ({
      variables: {
        slug: router.query.slug
      }
    })
  })
)(GroupPastEvents);
